/**
 * X ADS › CAMPAIGNS — /x-ads/campaigns
 *
 * The campaigns of the ads account in use, read live from X, with the last 7 days of results
 * from the reporting collection merged in when there are any. A campaign opens onto its line
 * items, and a line item onto the posts it promotes. Pause / resume / delete act on X directly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Film, Megaphone, Pause, Pencil, Play, Plus, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Input } from '@/components/Input';
import { StatusPill } from '@/components/StatusPill';
import { DeleteButton } from '@/components/x-ads/DeleteButton';
import { EditCampaign, EditLineItem } from '@/components/x-ads/EditDialogs';
import { RetryLine, XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import { useXConnection } from '@/lib/x-ads/connection';
import { dateOnly, dateTime, label, lastDays, microsMoney, money, num, postText, statusTone } from '@/lib/x-ads/format';
import { goalLabel, placementLabel, productLabel, usesMediaCreatives } from '@/lib/x-ads/lineItemRules';
import { XA_NEW } from '@/lib/x-ads/routes';
import { errMsg, xAdsService, type XAggregateRow, type XCampaign, type XLineItem, type XMediaCreative, type XPromotedPost } from '@/services/x-ads.service';

/** Pause / resume one campaign or line item, keeping the row's status in step. */
function StatusToggle({ status, onPause, onResume, onChanged }: {
  status?: string; onPause: () => Promise<unknown>; onResume: () => Promise<unknown>; onChanged: (s: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const active = status === 'ACTIVE';
  const run = async () => {
    setBusy(true);
    try {
      if (active) { await onPause(); onChanged('PAUSED'); toast.success('Paused'); }
      else { await onResume(); onChanged('ACTIVE'); toast.success('Running — it can now spend'); }
    } catch { /* the client already showed X's reason */ }
    finally { setBusy(false); }
  };
  return <span onClick={(e) => e.stopPropagation()}>
    <Button variant="ghost" size="sm" className="h-8" loading={busy} onClick={run} iconLeft={active ? <Pause size={13} /> : <Play size={13} />}>{active ? 'Pause' : 'Resume'}</Button>
  </span>;
}

export default function XAdsCampaigns() {
  const { uid, account } = useXConnection();
  const [campaigns, setCampaigns] = useState<XCampaign[] | null>(null);
  const [metrics, setMetrics] = useState<Record<string, XAggregateRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<XCampaign | null>(null);
  const [query, setQuery] = useState('');
  const currency = account?.currency;

  const loadMetrics = useCallback(async () => {
    if (!uid) return;
    try {
      const rows = await xAdsService.aggregate(uid, { level: 'campaign', ...lastDays(7, account?.timezone) }, true);
      setMetrics(Object.fromEntries(rows.map((r) => [r.entityId, r])));
    } catch { setMetrics({}); }
  }, [uid, account?.timezone]);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true); setError(null);
    try {
      const [list] = await Promise.all([xAdsService.campaigns(uid), loadMetrics()]);
      setCampaigns(list);
    } catch (e) { setError(errMsg(e, 'Unable to load campaigns.')); }
    finally { setLoading(false); }
  }, [uid, loadMetrics]);
  useEffect(() => { setCampaigns(null); setOpen(null); load(); }, [load]);

  const sync = async () => {
    if (!uid) return;
    setSyncing(true);
    try {
      const r = await xAdsService.syncReport(uid, 7, 'campaign');
      toast.success(`Results synced for ${r.entities} campaign${r.entities === 1 ? '' : 's'}`);
      await loadMetrics();
    } catch { /* toasted */ }
    finally { setSyncing(false); }
  };

  const setStatus = (id: string, s: string) => setCampaigns((list) => list?.map((c) => (c.id === id ? { ...c, entityStatus: s } : c)) ?? null);
  const visible = useMemo(() => (campaigns ?? []).filter((c) => `${c.name ?? ''} ${c.id}`.toLowerCase().includes(query.trim().toLowerCase())), [campaigns, query]);
  const hasMetrics = Object.keys(metrics).length > 0;

  return <XAdsShell section="Campaigns" description="Your X campaigns, their line items and the posts they promote."
    actions={<>
      <Button variant="ghost" onClick={load} loading={loading} iconLeft={<RefreshCw size={15} />}>Refresh</Button>
      <Button variant="secondary" onClick={sync} loading={syncing} iconLeft={<RefreshCw size={15} />}>Sync results</Button>
      <Link to={XA_NEW}><Button iconLeft={<Plus size={15} />}>New campaign</Button></Link>
    </>}>
    {account?.sandbox && <XAdsNotice title="Sandbox account">Campaigns here never serve. Sandbox accounts have no real results.</XAdsNotice>}
    {error && <RetryLine message={error} onRetry={load} />}
    {campaigns === null && !error && <div className="skeleton h-64 rounded-2xl" aria-label="Loading campaigns" />}
    {campaigns?.length === 0 && <EmptyState icon={<Megaphone size={28} />} title="No campaigns yet"
      description="Build a campaign, its line item, targeting and a promoted post in one flow. Everything starts paused so you can review it first."
      action={<Link to={XA_NEW}><Button iconLeft={<Plus size={15} />}>New campaign</Button></Link>} />}
    {!!campaigns?.some((c) => (c.reasonsNotServable ?? []).some((r) => /ADVERTISER_NOT_VERIFIED/i.test(r))) && <XAdsNotice title="X has not verified this advertiser yet" error>
      X lists "Rejected advertiser not verified" on these campaigns, so nothing serves even when resumed. Complete the advertiser
      verification for this ads account on ads.x.com (Account settings / Billing); X reviews it, then the campaigns can run.
    </XAdsNotice>}
    {!!campaigns?.length && <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-[220px] max-w-sm flex-1">
          <Input iconLeft={<Search size={15} />} aria-label="Search campaigns" placeholder="Search campaigns…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <p className="xa-muted">{hasMetrics ? 'Results: last 7 days from the last sync.' : 'No synced results yet — press "Sync results".'}</p>
      </div>
      <div className="xa-list">
        {visible.map((c) => {
          const m = metrics[c.id];
          const isOpen = open === c.id;
          return <div key={c.id}>
            <div className="xa-row cursor-pointer hover:bg-surface-alt/40" role="button" tabIndex={0} aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : c.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(isOpen ? null : c.id); } }}>
              {isOpen ? <ChevronDown size={16} className="shrink-0 text-text-muted" /> : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-semibold">{c.name || c.id}</span>
                  <StatusPill status={c.entityStatus ?? ''} label={label(c.entityStatus)} variant={statusTone(c.entityStatus)} />
                  {c.effectiveStatus && c.effectiveStatus !== c.entityStatus && c.effectiveStatus !== 'UNKNOWN' && <span className="xa-chip">{label(c.effectiveStatus)}</span>}
                </div>
                <p className="xa-muted">
                  Daily {microsMoney(c.dailyBudgetLocalMicro, c.currency ?? currency)}
                  {c.totalBudgetLocalMicro ? ` · Total ${microsMoney(c.totalBudgetLocalMicro, c.currency ?? currency)}` : ''}
                  {` · Created ${dateOnly(c.createdAt)} · ID ${c.id}`}
                </p>
                {c.servable === false && !!c.reasonsNotServable?.length && <p className="mt-0.5 flex items-center gap-1 text-[11px] text-warning"><AlertTriangle size={12} />Not serving: {c.reasonsNotServable.map(label).join(', ')}</p>}
              </div>
              {m && <div className="hidden gap-5 text-right text-xs lg:flex">
                {[['Impressions', num(m.impressions)], ['Engagements', num(m.engagements)], ['Clicks', num(m.clicks)], ['Spend', money(m.spend, c.currency ?? currency)]].map(([k, v]) =>
                  <div key={k}><p className="text-[10px] text-text-muted">{k}</p><p className="font-semibold tabular-nums">{v}</p></div>)}
              </div>}
              <span onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8" aria-label={`Edit campaign ${c.name ?? c.id}`} iconLeft={<Pencil size={13} />} onClick={() => setEditing(c)}>Edit</Button>
              </span>
              <StatusToggle status={c.entityStatus} onChanged={(s) => setStatus(c.id, s)}
                onPause={() => xAdsService.pauseCampaign(uid, c.id)} onResume={() => xAdsService.resumeCampaign(uid, c.id)} />
              <DeleteButton what="campaign" name={c.name} onDelete={() => xAdsService.deleteCampaign(uid, c.id)}
                onDone={() => setCampaigns((list) => list?.filter((x) => x.id !== c.id) ?? null)} />
            </div>
            {isOpen && <div className="xa-detail"><LineItems uid={uid} campaignId={c.id} currency={c.currency ?? currency} /></div>}
          </div>;
        })}
        {visible.length === 0 && <p className="p-8 text-center text-sm text-text-muted">No campaign matches "{query}".</p>}
      </div>
    </>}
    <EditCampaign uid={uid} campaign={editing} currency={editing?.currency ?? currency} onClose={() => setEditing(null)}
      onSaved={(saved) => { setCampaigns((list) => list?.map((x) => (x.id === saved.id ? saved : x)) ?? null); setEditing(null); }} />
  </XAdsShell>;
}

function LineItems({ uid, campaignId, currency }: { uid: string; campaignId: string; currency?: string }) {
  const [items, setItems] = useState<XLineItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<XLineItem | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setItems(await xAdsService.lineItems(uid, campaignId)); }
    catch (e) { setError(errMsg(e, 'Unable to load line items.')); }
  }, [uid, campaignId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = (id: string, s: string) => setItems((list) => list?.map((l) => (l.id === id ? { ...l, entityStatus: s } : l)) ?? null);

  if (error) return <RetryLine message={error} onRetry={load} />;
  if (!items) return <div className="skeleton h-16 rounded-lg" aria-label="Loading line items" />;
  if (items.length === 0) return <p className="xa-muted py-2">No line items in this campaign yet.</p>;
  return <div className="space-y-2">
    <EditLineItem uid={uid} lineItem={editing} currency={editing?.currency ?? currency} onClose={() => setEditing(null)}
      onSaved={(saved) => { setItems((list) => list?.map((x) => (x.id === saved.id ? saved : x)) ?? null); setEditing(null); }} />
    <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Line items</p>
    {items.map((l) => {
      const isOpen = open === l.id;
      const bid = l.bidStrategy === 'AUTO' || !l.bidStrategy ? 'Automatic bid' : `${label(l.bidStrategy)} bid ${microsMoney(l.bidAmountLocalMicro, l.currency ?? currency)}`;
      return <div key={l.id} className="rounded-lg border border-border bg-surface">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2">
          <button className="flex min-w-0 flex-1 items-start gap-2 text-left" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : l.id)}>
            {isOpen ? <ChevronDown size={15} className="mt-0.5 shrink-0 text-text-muted" /> : <ChevronRight size={15} className="mt-0.5 shrink-0 text-text-muted" />}
            <span className="min-w-0">
              <span className="flex flex-wrap items-center gap-2"><span className="truncate text-sm font-medium">{l.name || l.id}</span><StatusPill status={l.entityStatus ?? ''} label={label(l.entityStatus)} variant={statusTone(l.entityStatus)} /></span>
              <span className="xa-muted block">{[label(l.objective), productLabel(l.productType ?? ''), bid, l.goal && goalLabel(l.goal), (l.placements ?? []).map(placementLabel).join(', ')].filter(Boolean).join(' · ')}</span>
              <span className="xa-muted block">Starts {dateTime(l.startTime)}{l.endTime ? ` · Ends ${dateTime(l.endTime)}` : ' · No end date'} · ID {l.id}</span>
            </span>
          </button>
          <Button variant="ghost" size="sm" className="h-8" aria-label={`Edit line item ${l.name ?? l.id}`} iconLeft={<Pencil size={13} />} onClick={() => setEditing(l)}>Edit</Button>
          <StatusToggle status={l.entityStatus} onChanged={(s) => setStatus(l.id, s)}
            onPause={() => xAdsService.pauseLineItem(uid, l.id)} onResume={() => xAdsService.resumeLineItem(uid, l.id)} />
          <DeleteButton what="line item" name={l.name} onDelete={() => xAdsService.deleteLineItem(uid, l.id)}
            onDone={() => setItems((list) => list?.filter((x) => x.id !== l.id) ?? null)} />
        </div>
        {isOpen && <div className="border-t border-border px-3 py-2">
          {usesMediaCreatives(l.objective ?? '', l.productType ?? '') ? <MediaCreatives uid={uid} lineItemId={l.id} /> : <PromotedPosts uid={uid} lineItemId={l.id} />}
        </div>}
      </div>;
    })}
  </div>;
}

/** Videos running on a pre-roll / MEDIA line item (those take no posts). */
function MediaCreatives({ uid, lineItemId }: { uid: string; lineItemId: string }) {
  const [rows, setRows] = useState<XMediaCreative[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    try { setRows(await xAdsService.mediaCreatives(uid, lineItemId)); }
    catch (e) { setError(errMsg(e, 'Unable to load the videos of this line item.')); }
  }, [uid, lineItemId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <RetryLine message={error} onRetry={load} />;
  if (!rows) return <div className="skeleton h-12 rounded-lg" aria-label="Loading videos" />;
  if (rows.length === 0) return <p className="xa-muted">No videos in this line item. Add one from the campaign wizard (pre-roll and Media line items run videos, not posts).</p>;
  return <ul className="space-y-2">
    {rows.map((m) => <li key={m.id} className="flex items-start gap-3">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-surface-alt text-text-muted"><Film size={16} /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{m.mediaKey ?? m.accountMediaId}</p>
        <p className="xa-muted">{[m.creativeType && label(m.creativeType), m.approvalStatus && `Review: ${label(m.approvalStatus)}`, m.landingUrl].filter(Boolean).join(' · ')}</p>
      </div>
      <DeleteButton what="video from this line item" detach name={m.mediaKey ?? m.id}
        message="The video stops running in this line item. It stays in the media library."
        onDelete={() => xAdsService.removeMediaCreative(uid, m.id)} onDone={() => setRows((list) => list?.filter((x) => x.id !== m.id) ?? null)} />
    </li>)}
  </ul>;
}

function PromotedPosts({ uid, lineItemId }: { uid: string; lineItemId: string }) {
  const [rows, setRows] = useState<XPromotedPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    try { setRows(await xAdsService.promoted(uid, lineItemId)); }
    catch (e) { setError(errMsg(e, 'Unable to load promoted posts.')); }
  }, [uid, lineItemId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <RetryLine message={error} onRetry={load} />;
  if (!rows) return <div className="skeleton h-12 rounded-lg" aria-label="Loading promoted posts" />;
  if (rows.length === 0) return <p className="xa-muted">No posts promoted in this line item. Add one from the campaign wizard.</p>;
  return <ul className="space-y-2">
    {rows.map((p) => <li key={p.id} className="flex items-start gap-3">
      {p.post?.mediaUrls?.[0]
        ? <img src={p.post.mediaUrls[0]} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" loading="lazy" />
        : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-surface-alt text-[10px] text-text-muted">Post</span>}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm">{postText(p.post?.text) || p.post?.name || `Post ${p.tweetId}`}</p>
        <p className="xa-muted">Post {p.tweetId}{p.approvalStatus ? ` · Review: ${label(p.approvalStatus)}` : ''}{p.entityStatus ? ` · ${label(p.entityStatus)}` : ''}</p>
      </div>
      <DeleteButton what="post from this line item" detach name={p.tweetId}
        message="The post stops being promoted in this line item. The post itself stays on X."
        onDelete={() => xAdsService.unpromote(uid, p.id)} onDone={() => setRows((list) => list?.filter((x) => x.id !== p.id) ?? null)} />
    </li>)}
  </ul>;
}
